import { useEffect, useRef, useCallback } from 'react'
import useFetch, { CachePolicies } from 'use-http'
import { StatusResponse, UseBulkJobStatusOptions } from '../interfaces/BulkJobs'

export function useGetBulkJobStatus({
  bulkJobIds,
  onData,
  initialDelay = 5000,
  maxDelay = 600_000,
  factor = 2,
  maxTotalTime = 3_600_000,
}: UseBulkJobStatusOptions) {
  const cancelRef = useRef(false)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const startRef = useRef<number>()
  const delayRef = useRef(initialDelay)


  const allDone = (items: StatusResponse[]) =>
    items.length > 0 &&
    items.every(i => i.status === 'SUCCESS')

  const { get, data, abort } = useFetch<StatusResponse[]>({
    cachePolicy: CachePolicies.NO_CACHE,
    data: [],
  })

  const tick = useCallback(() => {
    if (Date.now() - (startRef.current ?? 0) >= maxTotalTime) {
      cancelRef.current = true
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      return
    }

    if (cancelRef.current) {
      return;
    }

    void get(`api/v2/bulkJobs/batch?bulkJobIds=${bulkJobIds.join(',')}`)

    delayRef.current = Math.min(delayRef.current * factor, maxDelay)
    timerRef.current = setTimeout(tick, delayRef.current)
  }, [bulkJobIds.join(','), maxDelay, factor, maxTotalTime, get])

  useEffect(() => {
    if (bulkJobIds.length === 0) return

    delayRef.current = initialDelay
    startRef.current = Date.now()
    cancelRef.current = false;

    setTimeout(tick, initialDelay)

    return () => {
      cancelRef.current = true
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      abort();
    }

  }, [bulkJobIds.join(','), initialDelay, tick])

  useEffect(() => {
    if (onData && Array.isArray(data)) {
      onData(data)
      if (allDone(data)) {
        cancelRef.current = true
        if (timerRef.current) {
          clearTimeout(timerRef.current)
          timerRef.current = null
        }
      }
    }
  }, [data])
}
